/**
 * The screen that stands between a reopened app and the tenant's data.
 *
 * Paints synchronously and unconditionally — never behind a plugin promise.
 * `shared/ota/confirmBundle.ts` confirms a freshly applied OTA bundle only once
 * `#root` has children, and the updater rolls the bundle back if it never does,
 * so a lock screen that waited on `checkBiometry()` before rendering could
 * revert a perfectly good update.
 */
import { Fingerprint, Loader2, ShieldAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { STANDALONE_TITLE } from '../styles/typography';

export type AppLockScreenStatus =
  /** Idle, waiting for the person to ask for the prompt. */
  | { kind: 'locked' }
  /** The system sheet is up. */
  | { kind: 'prompting' }
  /** They dismissed it, or the OS did. Costs nothing — offer it again. */
  | { kind: 'cancelled' }
  /** The device refused them. `remaining` counts down to the full-login wipe. */
  | { kind: 'failed'; remaining: number }
  /** No biometry and no passcode on this device. */
  | { kind: 'unavailable' };

export interface AppLockScreenProps {
  status: AppLockScreenStatus;
  onUnlock: () => void;
  onSignOut: () => void;
}

export function AppLockScreen({ status, onUnlock, onSignOut }: AppLockScreenProps) {
  const { t } = useTranslation(['auth', 'common']);
  const isPrompting = status.kind === 'prompting';

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-6 bg-background p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="app-lock-title"
      data-testid="app-lock-screen"
    >
      <div className="flex w-full max-w-sm flex-col items-center gap-6 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
          <Fingerprint className="h-8 w-8 text-primary" aria-hidden="true" />
        </div>

        <div className="space-y-2">
          {/* The lock renders outside the app shell — no sidebar, no
              PageHeader, no breadcrumbs — which is exactly the standalone
              role, the same one the auth screens use. */}
          <h1 id="app-lock-title" className={STANDALONE_TITLE}>
            {t('auth:appLock.screen.title')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('auth:appLock.screen.description')}
          </p>
        </div>

        {status.kind === 'cancelled' && (
          <p className="text-sm text-muted-foreground" data-testid="app-lock-cancelled">
            {t('auth:appLock.screen.cancelled')}
          </p>
        )}

        {status.kind === 'failed' && (
          <Alert variant="destructive" data-testid="app-lock-failed">
            <ShieldAlert className="h-4 w-4" />
            <AlertDescription>
              {t('auth:appLock.screen.failed', { count: status.remaining })}
            </AlertDescription>
          </Alert>
        )}

        {status.kind === 'unavailable' && (
          <Alert data-testid="app-lock-unavailable">
            <ShieldAlert className="h-4 w-4" />
            <AlertDescription>{t('auth:appLock.screen.unavailable')}</AlertDescription>
          </Alert>
        )}

        <div className="flex w-full flex-col gap-2">
          {status.kind !== 'unavailable' && (
            <Button
              className="w-full"
              onClick={onUnlock}
              disabled={isPrompting}
              data-testid="app-lock-unlock-button"
            >
              {isPrompting ? (
                <Loader2 className="me-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Fingerprint className="me-2 h-4 w-4" aria-hidden="true" />
              )}
              {t('auth:appLock.screen.unlock')}
            </Button>
          )}
          {/*
            Always offered. Dismissing the prompt must never be a dead end: iOS
            raises its own cancels for an incoming call or the screen locking,
            and somebody who genuinely cannot pass the device check still needs
            a way to reach the login page.
          */}
          <Button
            variant="ghost"
            className="w-full"
            onClick={onSignOut}
            disabled={isPrompting}
            data-testid="app-lock-sign-out-button"
          >
            {t('auth:appLock.screen.signOut')}
          </Button>
        </div>
      </div>
    </div>
  );
}
